import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';

export interface AnchoredPosition {
  /** False until the panel has been measured. Render it hidden, not absent — see below. */
  ready: boolean;
  /** Which side it actually opened on, after flipping. */
  placement: 'bottom' | 'top';
  /** Clamped so a long list scrolls instead of running off the viewport. */
  maxHeight: number;
  style: React.CSSProperties;
}

interface Options {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLElement | null>;
  /** Gap between anchor and panel, in px. */
  gap?: number;
  /** Keep this much clear of the viewport edge. */
  viewportPadding?: number;
  /** Below this, flipping is preferred over clamping. */
  minPanelHeight?: number;
  /**
   * Floor on the panel width, so a compact inline trigger does not truncate its own options.
   * The panel still matches a wider anchor exactly.
   */
  minPanelWidth?: number;
}

/**
 * Positions a floating panel against an anchor: flip, shift, clamp.
 *
 * <b>Ported from SalesIQ's `useAnchoredPosition`</b> (`libs/shared-ui/src/hooks/`), which exists
 * because the same flip formula had been copy-pasted into five components and one of them had
 * quietly stopped flipping at all. The control plane has no Tailwind and cannot import that lib, so
 * this is the same behaviour in plain CSS.
 *
 * <b>Measures the REAL rendered height</b> rather than estimating `options.length * 40`. An
 * estimate is wrong the moment an option wraps to two lines, and wrong in the direction that
 * clips the list.
 *
 * <b>`ready` is false until the first measure — render the panel HIDDEN, not `null`.</b> The refs
 * and the search input must exist before the open effect tries to focus them. The pre-measure style
 * uses `opacity: 0` and NOT `visibility: hidden`, because the latter makes children unfocusable and
 * the focus-on-open would silently do nothing.
 */
export function useAnchoredPosition({
  open,
  anchorRef,
  floatingRef,
  gap = 4,
  viewportPadding = 8,
  minPanelHeight = 140,
  minPanelWidth = 200,
}: Options): AnchoredPosition {
  const [state, setState] = useState<AnchoredPosition>({
    ready: false,
    placement: 'bottom',
    maxHeight: 0,
    style: { position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' },
  });

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;

    const a = anchor.getBoundingClientRect();
    const panelHeight = floating.scrollHeight;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const spaceBelow = vh - a.bottom - gap - viewportPadding;
    const spaceAbove = a.top - gap - viewportPadding;

    // Flip only when below genuinely cannot work AND above is better. Preferring "whichever is
    // larger" would make the panel jump sides on any small scroll.
    const flip = spaceBelow < Math.min(panelHeight, minPanelHeight) && spaceAbove > spaceBelow;
    const placement: 'bottom' | 'top' = flip ? 'top' : 'bottom';
    const maxHeight = Math.max(minPanelHeight, Math.floor(flip ? spaceAbove : spaceBelow));

    // Match the anchor's width — except when the anchor is a compact inline trigger in a toolbar,
    // where matching it truncates every option to the width of the shortest one. A floor lets the
    // panel grow past the trigger; it is never allowed past the viewport.
    const width = Math.min(
      Math.max(a.width, minPanelWidth),
      Math.max(minPanelWidth, vw - viewportPadding * 2));
    let left = a.left;
    if (left + width > vw - viewportPadding) left = Math.max(viewportPadding, vw - viewportPadding - width);
    if (left < viewportPadding) left = viewportPadding;

    setState({
      ready: true,
      placement,
      maxHeight,
      style: {
        position: 'fixed',
        left: Math.round(left),
        top: flip ? undefined : Math.round(a.bottom + gap),
        bottom: flip ? Math.round(vh - a.top + gap) : undefined,
        width: Math.round(width),
        maxHeight,
      },
    });
  }, [anchorRef, floatingRef, gap, viewportPadding, minPanelHeight, minPanelWidth]);

  // Layout effect so the first paint is already positioned — a useEffect here shows one frame at
  // the top-left corner before snapping into place.
  useLayoutEffect(() => {
    if (!open) {
      setState((s) => (s.ready ? { ...s, ready: false } : s));
      return;
    }
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;

    // `true` = capture, so a scroll in ANY ancestor repositions, not just the window.
    const onScroll = () => measure();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    // Follows the mobile keyboard, which resizes the visual viewport without firing `resize`.
    window.visualViewport?.addEventListener('resize', onScroll);
    window.visualViewport?.addEventListener('scroll', onScroll);

    const observer = new ResizeObserver(() => measure());
    if (floatingRef.current) observer.observe(floatingRef.current);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.visualViewport?.removeEventListener('resize', onScroll);
      window.visualViewport?.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [open, measure, floatingRef]);

  return state;
}
